import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useLanguage } from '../LanguageContext';

const FAQPage = () => {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);

  const faqItems = t('faq.items') || [];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-cyan-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider mb-3 md:mb-4">
            {t('faq.badge')}
          </span>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4 px-2">
            {t('faq.title')} <span className="text-cyan-500">{t('faq.titleHighlight')}</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-xs md:text-base px-4">
            {t('faq.subtitle')}
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4 md:space-y-5 max-w-3xl mx-auto">
          {faqItems.map((item, index) => (
            <div 
              key={index}
              className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl overflow-hidden transition-all duration-300 hover:border-cyan-600/40 hover:bg-zinc-900/60"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-5 md:px-6 py-4 md:py-5 flex items-center justify-between text-left transition-colors"
                data-testid={`faq-toggle-${index}`}
              >
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-4 h-4 md:w-5 md:h-5 text-cyan-400" />
                  </div>
                  <span className="text-white font-medium text-sm md:text-base pr-2">{item.question}</span>
                </div>
                <ChevronDown 
                  className={`w-5 h-5 text-cyan-400/70 transition-transform duration-300 flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`}
                />
              </button>
              
              <div 
                className={`overflow-hidden transition-all duration-500 ${openIndex === index ? 'max-h-[2000px]' : 'max-h-0'}`}
              >
                <div className="px-5 md:px-6 pb-5 pl-[4.25rem] md:pl-[4.5rem] space-y-3">
                  {item.answer.split('\n\n').map((paragraph, pi) => (
                    <p key={pi} className="text-gray-400 leading-relaxed text-sm md:text-base whitespace-pre-line">{paragraph}</p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-8 md:mt-12 text-center p-5 md:p-8 bg-gradient-to-br from-cyan-600/20 to-cyan-900/10 border border-cyan-600/30 rounded-xl md:rounded-2xl">
          <h3 className="text-lg md:text-xl font-bold text-white mb-1.5 md:mb-2">{t('faq.contactTitle')}</h3>
          <p className="text-gray-400 mb-4 text-xs md:text-base">{t('faq.contactSubtitle')}</p>
          <a 
            href="https://discord.gg/TXEa2x6mjZ" 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-all duration-200 text-sm md:text-base"
            data-testid="faq-discord-btn"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            {t('faq.contactButton')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
